// @ts-nocheck
import { Grid } from '@mui/material';
import { useTheme } from '@mui/styles';
import { BasicButton, SubmitButton } from '@tassat/button';
import { AccountType } from '@tassat/common-definitions';
import { SimpleErrorMessage, TextField } from '@tassat/formik-input';
import { CustomerModifyFirmAccountReq } from '@tassat/fred-definitions';
import { ApplicationMuiTheme } from '@tassat/payments-themes/dist/types/theme';
import { ActionTypes, doesWorkflowRejectCodeExist } from '@tassat/ui-definitions';
import {
    ConfirmationWorkflowAction,
    ConfirmationWorkflowConsumer,
    ConfirmationWorkflowContext,
    ConfirmationWorkflowReducer,
    ConfirmationWorkflowState,
    DEFAULT_INITIAL_STATE
} from '@tassat/workflow';
import React from 'react';

import { muiStyles } from './styles';
import { IFormProps } from './types';

export const FormContents = (props: IFormProps) => {
    const {
        values,
        errors,
        dirty,
        isSubmitting,
        isValid,
        submitForm,
        submitError,
        formProps,
        dispatchDdaAccountAction,
        setFieldValue
    } = props;

    const { actionType } = formProps;

    const classes = muiStyles(props);
    const theme = useTheme<ApplicationMuiTheme>();
    const editAccount = actionType === ActionTypes.edit && formProps.editAccount || undefined;
    const associatedWallets = actionType === ActionTypes.edit && formProps.associatedWallets || undefined;
    const [
        confirmationWorkflowState,
        dispatchConfirmationWorkflowAction
    ] = React.useReducer<React.Reducer<
        ConfirmationWorkflowState,
        ConfirmationWorkflowAction
    >>(ConfirmationWorkflowReducer, DEFAULT_INITIAL_STATE);
    const confirmationContext = React.useMemo(() => ({
        confirmationWorkflowState,
        dispatchConfirmationWorkflowAction
    }), [ confirmationWorkflowState, dispatchConfirmationWorkflowAction ]);

    const closeAccountValidation = async ({ editAccount, associatedWallets }): Promise<boolean> => {
        const promises = [];
        let validationError = '';
        associatedWallets.forEach((wallet) => {
            const balanceExists = wallet.walletStats
                .some((stat) => stat.CurrentPosition && parseFloat(stat.CurrentPosition) > 0);

            if (balanceExists) {
                validationError = 'Cannot close BD Account with linked accounts containing positive balance';
            } else {
                // make history request to check on open transactions
                if (actionType !== ActionTypes.edit) {
                    return true;
                }

                if (wallet?.wallet?.Account) {
                    promises.push(formProps.getTransactions({
                        Condition: `isCompleted|EQ|false:wallet|EQ|${wallet.wallet.Account}`,
                        Sort: 'AuditTime|DESC',
                        PageNo: 1,
                        RecordPerPage: 4
                    }));
                }
            }
        });

        if (promises.length) {
            try {
                const responses = await Promise.all(promises);
                responses.some((response) => {
                    if (response.data && response.data.TotalCount > 0) {
                        validationError = 'Cannot close BD Account with linked accounts containing pending transactions.';
                    }
                });
            } catch (error) {
                validationError = 'Could not validate if there are open transactions, please try again';
            }
        }

        if (validationError) {
            const validationErrorAction: ConfirmationWorkflowAction = {
                type: 'VALIDATION_FAILED',
                workflowConfiguration: {
                    validationTitle: `Failed to Close Account ${editAccount?.AccountName}`,
                    validationMessage: validationError
                }
            };
            dispatchConfirmationWorkflowAction(validationErrorAction);

            return true;
        }

        const validationSuccessfulAction: ConfirmationWorkflowAction = {
            type: 'VALIDATION_SUCCESS',
            workflowConfiguration: {
                selectedItem: {
                    editAccount: editAccount,
                    associatedWallets: associatedWallets
                },
                actionType: ActionTypes.close,
                confirmTitle: `Confirm Closing Account ${editAccount?.AccountName}`,
                confirmBody: `Please confirm closing the account ${editAccount?.AccountName}.`
            }
        };
        dispatchConfirmationWorkflowAction(validationSuccessfulAction);

        return false;
    };

    const closeAccount = async ({ editAccount, associatedWallets }) => {
        if (actionType !== ActionTypes.edit) {
            return;
        }
        try {
            let response = { data: undefined };
            const sendValues: Omit<
                CustomerModifyFirmAccountReq,
                'RequestId' |
                'RequesterId' |
                'FirmId' |
                'AccountId' |
                'Account' |
                'AccountName1' |
                'AccountName2' |
                'ShortName' |
                'WalletName1' |
                'WalletName2' |
                'UUID' |
                'IsWire' |
                'IsLiquidityFacility' |
                'ACGId1' |
                'ACGId2' |
                'ACGId3' |
                'AccessControl' |
                'IsPrimary'
            > & { LinkedAccountId: string } = {
                AccountType: AccountType.CLIENT_BDA,
                AccountName: editAccount.AccountName,
                Email: editAccount.Email,
                Notes: editAccount.Notes,
                IsLocked: editAccount.IsLocked,
                IsClosed: true,
                LinkedAccountId: editAccount.LinkedAccountId
            };

            response = await formProps.updateFirmAccount(editAccount.FirmId, editAccount.AccountId, sendValues);

            if (response.data && response.data?.Status === 48) {
                const serverSuccessAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_SUCCESS',
                    workflowConfiguration: {
                        successTitle: `Successfully Closed Account ${editAccount.AccountName}`,
                        successBody: 'Successfully closed the account ' +
                            `${editAccount.AccountName}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverSuccessAction);
            } else if (response.data && doesWorkflowRejectCodeExist(response.data.Status)) {
                const serverErrorAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_ERROR',
                    workflowConfiguration: {
                        failureTitle: `Failed to Close Account ${editAccount.AccountName}`,
                        failureBody: `Could not close account ${editAccount.AccountName}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverErrorAction);
            }
        } catch (error) {
            const serverErrorAction: ConfirmationWorkflowAction = {
                type: 'SERVER_ERROR',
                workflowConfiguration: {
                    failureTitle: `Failed to Close Account ${editAccount.AccountName}`,
                    failureBody: 'Something went wrong closing the account ' +
                        `${editAccount.AccountName}.`
                }
            };
            dispatchConfirmationWorkflowAction(serverErrorAction);
        }
    };

    const reopenAccountValidation = ({ editAccount, associatedWallets }): boolean => {
        const validationSuccessfulAction: ConfirmationWorkflowAction = {
            type: 'VALIDATION_SUCCESS',
            workflowConfiguration: {
                selectedItem: {
                    editAccount: editAccount,
                    associatedWallets: associatedWallets
                },
                actionType: ActionTypes.reopen,
                confirmTitle: `Confirm Reopening Account ${editAccount.AccountName}`,
                confirmBody: 'Please confirm reopening the account ' +
                    `${editAccount.AccountName}.`
            }
        };
        dispatchConfirmationWorkflowAction(validationSuccessfulAction);

        return false;
    };

    const reopenAccount = async ({ editAccount, associatedWallets }) => {
        if (actionType !== ActionTypes.edit) {
            return;
        }
        try {
            let response = { data: undefined };
            const sendValues: Omit<
                CustomerModifyFirmAccountReq,
                'RequestId' |
                'RequesterId' |
                'FirmId' |
                'AccountId' |
                'Account' |
                'AccountName1' |
                'AccountName2' |
                'ShortName' |
                'WalletName1' |
                'WalletName2' |
                'UUID' |
                'IsWire' |
                'IsLiquidityFacility' |
                'ACGId1' |
                'ACGId2' |
                'ACGId3' |
                'AccessControl' |
                'IsPrimary'
            > & { LinkedAccountId: string } = {
                AccountType: AccountType.CLIENT_BDA,
                AccountName: editAccount.AccountName,
                Email: editAccount.Email,
                Notes: editAccount.Notes,
                IsLocked: editAccount.IsLocked,
                IsClosed: false,
                LinkedAccountId: editAccount.LinkedAccountId
            };

            response = await formProps.updateFirmAccount(editAccount.FirmId, editAccount.AccountId, sendValues);

            if (response.data && response.data?.Status === 48) {
                const serverSuccessAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_SUCCESS',
                    workflowConfiguration: {
                        successTitle: `Successfully Reopened Account ${editAccount.AccountName}`,
                        successBody: 'Successfully reopened the account ' +
                            `${editAccount.AccountName}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverSuccessAction);
            } else if (response.data && doesWorkflowRejectCodeExist(response.data.Status)) {
                const serverErrorAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_ERROR',
                    workflowConfiguration: {
                        failureTitle: `Failed to Reopen Account ${editAccount.AccountName}`,
                        failureBody: `Could not reopen account ${editAccount.AccountName}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverErrorAction);
            }
        } catch (error) {
            const serverErrorAction: ConfirmationWorkflowAction = {
                type: 'SERVER_ERROR',
                workflowConfiguration: {
                    failureTitle: `Failed to Reopen Account ${editAccount.AccountName}`,
                    failureBody: 'Something went wrong reopening the account ' +
                        `${editAccount.AccountName}.`
                }
            };
            dispatchConfirmationWorkflowAction(serverErrorAction);
        }
    };

    React.useEffect(() => {
        // set initial confirmation settings
        const configureConfirmationWorkflowAction: ConfirmationWorkflowAction = {
            type: 'CONFIGURE_WORKFLOW',
            workflowConfiguration: {
                selectedItem: {
                    editAccount: editAccount,
                    associatedWallets: associatedWallets
                },
                setSelectedItem: () => {
                    return {
                        editAccount: editAccount,
                        associatedWallets: associatedWallets
                    };
                },
                preActionValidationAndConfig: {
                    [ActionTypes.close]: closeAccountValidation,
                    [ActionTypes.reopen]: reopenAccountValidation
                },
                onConfirm: {
                    [ActionTypes.close]: closeAccount,
                    [ActionTypes.reopen]: reopenAccount
                }
            }
        };
        dispatchConfirmationWorkflowAction(configureConfirmationWorkflowAction);
    }, []);

    const renderButton = () => {
        if (actionType === ActionTypes.add || (actionType === ActionTypes.edit && !editAccount)) {
            return null;
        }

        return (
            actionType === ActionTypes.edit && !editAccount?.IsClosed ? (
                <Grid item>
                    <BasicButton
                        onClick={() => editAccount && associatedWallets && closeAccountValidation({
                            editAccount: editAccount,
                            associatedWallets: associatedWallets
                        })}
                        text='Close BD Account'
                        type='action'
                    />
                </Grid>
            ) : (
                <Grid item>
                    <BasicButton
                        onClick={() => editAccount && associatedWallets && reopenAccountValidation({
                            editAccount: editAccount,
                            associatedWallets: associatedWallets
                        })}
                        text='Reopen BD Account'
                        type='action'
                    />
                </Grid>
            )
        );
    };

    return (
        <ConfirmationWorkflowContext.Provider value={confirmationContext}>
            <form className={classes.flexColumn}>
                <Grid
                    container
                    direction='column'
                    spacing={0}
                >
                    <Grid
                        className={classes.fullWidth}
                        container
                        item
                        spacing={2}
                        xs={12}
                    >
                        <Grid
                            item
                            xs={6}
                        >
                            <TextField
                                disabled={editAccount?.IsClosed}
                                errors={errors}
                                id='AccountName'
                                label='BD Account Name'
                                maxLength={64}
                                name='AccountName'
                                placeholder='BD Account Name'
                                values={values}
                            />
                        </Grid>
                        <Grid
                            item
                            xs={6}
                        >
                            <TextField
                                customOnChange={(e) => {
                                    dispatchDdaAccountAction({ type: 'FIELD_CHANGED' });
                                    setFieldValue('Account', e.target.value);
                                }}
                                disabled={editAccount?.IsClosed}
                                errors={errors}
                                id='Account'
                                label='BD Account Number'
                                maxLength={64}
                                name='Account'
                                placeholder='BD Account Number'
                                values={values}
                            />
                        </Grid>
                    </Grid>
                    <Grid
                        className={classes.fullWidth}
                        container
                        item
                        spacing={2}
                        xs={12}
                    >
                        <Grid
                            item
                            xs={6}
                        >
                            <TextField
                                disabled={editAccount?.IsClosed}
                                errors={errors}
                                id='Email'
                                label='Notification Email'
                                maxLength={64}
                                name='Email'
                                placeholder='Notification Email'
                                values={values}
                            />
                        </Grid>
                        <Grid
                            item
                            xs={6}
                        >
                            <TextField
                                disabled={editAccount?.IsClosed}
                                errors={errors}
                                id='Notes'
                                label='Notes'
                                maxLength={50}
                                name='Notes'
                                placeholder='Notes'
                                values={values}
                            />
                        </Grid>
                        <Grid
                            alignItems='center'
                            className={classes.submitButtonContainer}
                            container
                            item
                            justifyContent='space-evenly'
                            xs={12}
                        >
                            {renderButton()}
                            <Grid
                                className={classes.submitButton}
                                container
                                direction='column'
                                item
                            >
                                <SubmitButton
                                    disabled={!dirty ||
                                        isSubmitting ||
                                        !isValid ||
                                        editAccount?.IsClosed}
                                    onClick={submitForm}
                                    text={actionType === ActionTypes.edit ? 'Save Changes' : 'Create BD Account'}
                                    type='submit'
                                />
                                <SimpleErrorMessage
                                    className={classes.submitError}
                                    error={submitError}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </form>
            <ConfirmationWorkflowConsumer />
        </ConfirmationWorkflowContext.Provider>
    );
};